import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Wallet, Copy, ExternalLink, RefreshCw, Search } from "lucide-react";

interface AddrRow {
  id: string;
  user_id: string;
  address: string;
  network: string;
  address_index: number | null;
  created_at: string;
  profile?: { email: string | null; full_name: string | null; numeric_id: string | null } | null;
}

const AdminUserAddresses = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState<AddrRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const { data: addrs } = await supabase
      .from("user_crypto_addresses")
      .select("id, user_id, address, network, address_index, created_at")
      .order("created_at", { ascending: false });

    const list = (addrs || []) as AddrRow[];
    const userIds = Array.from(new Set(list.map(r => r.user_id)));
    if (userIds.length > 0) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("user_id, email, full_name, numeric_id")
        .in("user_id", userIds);
      const map = new Map((profs || []).map((p: any) => [p.user_id, p]));
      list.forEach(r => { r.profile = map.get(r.user_id) || null; });
    }
    setRows(list);
    setLoading(false);
  };

  const copy = (t: string) => {
    navigator.clipboard.writeText(t);
    toast({ title: "تم النسخ" });
  };

  const filtered = rows.filter(r => {
    const s = q.toLowerCase().trim();
    if (!s) return true;
    return (
      r.address.toLowerCase().includes(s) ||
      r.user_id.toLowerCase().includes(s) ||
      r.profile?.email?.toLowerCase().includes(s) ||
      r.profile?.full_name?.toLowerCase().includes(s) ||
      r.profile?.numeric_id?.includes(s)
    );
  });

  return (
    <Card className="glass">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Wallet className="w-5 h-5 text-primary" />
            عناوين المستخدمين ({rows.length})
          </CardTitle>
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="بحث بالعنوان، الإيميل، الاسم، أو المعرف..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pr-10 bg-input border-border"
          />
        </div>

        {loading ? (
          <p className="text-center text-muted-foreground py-8">جاري التحميل...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">لا توجد عناوين</p>
        ) : (
          filtered.map((r) => (
            <div key={r.id} className="p-3 rounded-lg border border-border bg-secondary/30 space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-foreground text-sm truncate">
                    {r.profile?.full_name || r.profile?.email || r.user_id.slice(0, 8)}
                  </p>
                  {r.profile?.email && (
                    <p className="text-xs text-muted-foreground truncate">{r.profile.email}</p>
                  )}
                  {r.profile?.numeric_id && (
                    <p className="text-xs text-muted-foreground font-mono">ID: {r.profile.numeric_id}</p>
                  )}
                </div>
                <Badge variant="outline" className="shrink-0">
                  {r.network} {r.address_index != null && `(#${r.address_index})`}
                </Badge>
              </div>
              <div className="flex items-center gap-1.5 p-2 bg-background/50 rounded" dir="ltr">
                <span className="text-xs text-primary font-mono break-all flex-1">{r.address}</span>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={() => copy(r.address)}>
                  <Copy className="w-3 h-3" />
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" asChild>
                  <a
                    href={`https://polygonscan.com/address/${r.address}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Etherscan"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default AdminUserAddresses;
